import { useState, useEffect } from "react";
import { Download, ExternalLink, Sparkles, Tag, CheckCircle2, Lock, Eye, EyeOff, Loader2, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { getCategory } from "../lib/categories";
import { useCategories } from "../hooks/useCategories";
import { fileUrl, revealDocument, setDocumentProperty } from "../lib/api";
import { AddressInput } from "./AddressInput";
import { VersionHistory } from "./VersionHistory";
import { MediaPlayer } from "./MediaPlayer";
import { ReleaseRules } from "./ReleaseRules";
import { useSecurity } from "../context/SecurityContext";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { tr } from "../lib/i18n";

// On-screen redaction: SSNs, account/card/policy numbers and any digit run of 4+ keep only the last 2 digits.
export const maskSensitive = (text) => String(text ?? "").replace(/\b\d{3}-\d{2}-\d{4}\b/g, "•••-••-••••").replace(/\d[\d\s-]{3,}\d/g, (m) => { const digits = m.replace(/\D/g, ""); return digits.length < 4 ? m : "•".repeat(Math.max(2, digits.length - 2)) + digits.slice(-2); });

const PREVIEWABLE = (ct) => {
  const t = (ct || "").split(";")[0].trim().toLowerCase();
  return t === "application/pdf" || t === "text/plain" || t.startsWith("image/");
};

export const DocumentInspector = ({ doc, open, onClose, onOverride, onUpdated, onDelete, readOnly = false }) => {
  const { role } = useSecurity();
  const { all: categories } = useCategories();
  const [category, setCategory] = useState(doc?.category);
  const [revealed, setRevealed] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [masked, setMasked] = useState(() => localStorage.getItem("hv_mask_sensitive") === "1");
  const [property, setProperty] = useState(doc?.property_address || "");
  const [savingProp, setSavingProp] = useState(false);

  useEffect(() => {
    setCategory(doc?.category);
    setRevealed(null);
    setProperty(doc?.property_address || "");
  }, [doc]);

  if (!doc) return null;

  const saveProperty = async () => {
    setSavingProp(true);
    try { const d = await setDocumentProperty(doc.id, property.trim()); onUpdated?.(d); toast.success(property.trim() ? tr("Property saved") : tr("Property cleared")); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setSavingProp(false); }
  };

  const cat = getCategory(doc.category);
  const CatIcon = cat.icon;
  const confidence = Math.round((doc.confidence || 0) * 100);
  const changed = category !== doc.category;
  const encrypted = doc.fields_encrypted;
  const displayFields = revealed || doc.extracted_fields || {};

  const doReveal = async () => {
    setRevealing(true);
    try {
      const { extracted_fields } = await revealDocument(doc.id);
      setRevealed(extracted_fields);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reveal");
    } finally {
      setRevealing(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        data-testid="document-inspector"
        className="w-full sm:max-w-lg overflow-y-auto p-0"
        onInteractOutside={(e) => { if (e.target?.closest?.("[data-sonner-toaster]")) e.preventDefault(); }}
      >
        <div className={`h-1.5 w-full ${cat.dot}`} />
        <div className="p-6">
          <SheetHeader className="text-left space-y-0">
            <div className="flex items-center gap-3 mb-3">
              <div className={`h-12 w-12 rounded-xl grid place-items-center ${cat.tint}`}>
                <CatIcon className="h-6 w-6" style={{ color: cat.iconColor }} />
              </div>
              <Badge className={`${cat.badge} border-0 capitalize`}>{tr(cat.label)}</Badge>
            </div>
            <SheetTitle
              data-testid="inspector-title"
              className="font-serif text-2xl leading-snug"
            >
              {doc.title}
            </SheetTitle>
            <p className="font-mono text-xs text-muted-foreground pt-1">
              {doc.original_filename} · {doc.file_size}
            </p>
          </SheetHeader>

          {/* AI confidence */}
          <div className="mt-5 rounded-xl border border-border bg-muted/40 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-mono text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5 text-primary" /> {tr("AI Confidence")}
              </span>
              <span className="font-mono font-semibold text-sm">{confidence}%</span>
            </div>
            <div className="h-2 rounded-full bg-border overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${confidence}%` }}
              />
            </div>
          </div>

          {/* Summary */}
          <div className="mt-5">
            <h4 className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-2">
              {tr("Summary")}
            </h4>
            <button type="button" onClick={() => setMasked((m) => { localStorage.setItem("hv_mask_sensitive", m ? "0" : "1"); return !m; })} aria-pressed={masked} data-testid="inspector-mask-toggle" title="Hide account numbers, SSNs and other long numbers on screen — handy when sharing your screen or sitting with an advisor"
              className={`mb-2 inline-flex items-center gap-1 rounded-full border px-2 h-6 text-[11px] transition-colors ${masked ? "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/40 dark:text-amber-200" : "border-border text-muted-foreground hover:border-primary hover:text-foreground"}`}>
              {masked ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />} {masked ? "Numbers hidden" : "Hide sensitive numbers"}
            </button>
            <p data-testid="inspector-summary" className="text-sm leading-relaxed text-foreground/90">
              {masked ? maskSensitive(doc.summary || "No summary available.") : (doc.summary || "No summary available.")}
            </p>
          </div>

          {/* Extracted fields */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs uppercase tracking-wider font-mono text-muted-foreground flex items-center gap-1.5">
                {encrypted && <Lock className="h-3.5 w-3.5 text-rose-500" />}
                {encrypted ? "Encrypted Details" : "Extracted Details"}
              </h4>
              {encrypted && !readOnly && (
                <button
                  data-testid={revealed ? "hide-secrets-btn" : "reveal-secrets-btn"}
                  onClick={() => (revealed ? setRevealed(null) : doReveal())}
                  disabled={revealing}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
                >
                  {revealing ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : revealed ? (
                    <EyeOff className="h-3.5 w-3.5" />
                  ) : (
                    <Eye className="h-3.5 w-3.5" />
                  )}
                  {revealed ? "Hide" : "Reveal"}
                </button>
              )}
            </div>
            <div
              data-testid="inspector-extracted-fields-list"
              className="rounded-xl border border-border divide-y divide-border overflow-hidden"
            >
              {Object.entries(displayFields).map(([k, v]) => (
                <div key={k} className="flex items-start justify-between gap-4 px-4 py-2.5 bg-card">
                  <span className="text-sm text-muted-foreground">{k}</span>
                  <span className={`text-sm font-mono font-medium text-right ${encrypted && !revealed ? "tracking-widest text-rose-500" : ""}`}>
                    {masked ? maskSensitive(String(v ?? "")) : v}
                  </span>
                </div>
              ))}
              {Object.keys(displayFields).length === 0 && (
                <div className="px-4 py-3 text-sm text-muted-foreground bg-card">
                  {tr("No structured fields extracted.")}
                </div>
              )}
            </div>
            {encrypted && (
              <p className="mt-1.5 text-[11px] text-muted-foreground flex items-center gap-1">
                <Lock className="h-3 w-3" /> {tr("Encrypted at rest — revealed only inside your unlocked vault.")}
              </p>
            )}
          </div>

          {/* Tags */}
          {doc.tags?.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              {doc.tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 text-xs rounded-full bg-secondary text-secondary-foreground px-2.5 py-1"
                >
                  <Tag className="h-3 w-3" /> {t}
                </span>
              ))}
            </div>
          )}

          {/* Property this document belongs to */}
          {!readOnly && (
          <div className="mt-4 rounded-xl border border-border bg-card p-4" data-testid="inspector-property">
            <h4 className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-2 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-primary" /> {tr("Property this relates to")}</h4>
            <div className="flex items-center gap-2">
              <AddressInput key={doc.id} testId="inspector-property-address" value={property} onChange={setProperty} placeholder={tr("Home or property address (deeds, mortgages, policies)")} className="bg-background h-9" wrapClass="flex-1 min-w-0" />
              <Button data-testid="save-property-btn" size="sm" variant="outline" className="h-9 shrink-0" disabled={property === (doc.property_address || "") || savingProp} onClick={saveProperty}>
                {savingProp ? <Loader2 className="h-4 w-4 animate-spin" /> : tr("Save")}
              </Button>
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">{tr("Tagged documents group under Accounts & policies → By property, so you can see which policies cover which home.")}</p>
          </div>
          )}

          {/* Category override */}
          {!readOnly && (
          <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-4">
            <h4 className="text-xs uppercase tracking-wider font-mono text-primary mb-2.5">
              {tr("Re-file Category")}
            </h4>
            <div className="flex items-center gap-2">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="inspector-category-override" className="bg-card">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.key} value={c.key}>
                      {tr(c.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                data-testid="save-category-override-btn"
                disabled={!changed}
                onClick={() => onOverride(doc.id, category)}
                className="gap-1.5 bg-primary hover:bg-primary/90 shrink-0"
              >
                <CheckCircle2 className="h-4 w-4" /> {tr("Save")}
              </Button>
            </div>
          </div>
          )}

          <MediaPlayer doc={doc} readOnly={readOnly} onUpdated={(d) => onUpdated?.(d)} />
          {!readOnly && (role === "owner" || role === "admin") && <ReleaseRules doc={doc} onSaved={(d) => onUpdated?.(d)} />}

          <VersionHistory doc={doc} readOnly={readOnly} />

          <div className="mt-6 space-y-2">
            <div className="flex gap-2">
              {doc.storage_path ? (
                <>
                  <Button asChild variant="outline" className="flex-1 gap-2" data-testid="inspector-download-btn">
                    <a href={fileUrl(doc.storage_path)} download={doc.original_filename || true}>
                      <Download className="h-4 w-4" /> {tr("Download")}
                    </a>
                  </Button>
                  {PREVIEWABLE(doc.content_type) && (
                    <Button asChild variant="outline" className="flex-1 gap-2" data-testid="inspector-preview-btn">
                      <a href={fileUrl(doc.storage_path, { inline: true })} target="_blank" rel="noreferrer">
                        <Eye className="h-4 w-4" /> {tr("Preview")}
                      </a>
                    </Button>
                  )}
                </>
              ) : (
                <Button variant="outline" className="flex-1 gap-2" disabled>
                  <ExternalLink className="h-4 w-4" /> {tr("Sample (no file)")}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {!readOnly && onDelete && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" data-testid="inspector-delete-btn" className="flex-1 gap-2 text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive">
                      <Trash2 className="h-4 w-4" /> {tr("Delete")}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="inspector-delete-confirm">
                    <AlertDialogHeader>
                      <AlertDialogTitle>{tr("Move to Trash?")}</AlertDialogTitle>
                      <AlertDialogDescription>
                        “{doc.title}” {tr("will go to Trash, where it can be restored for 30 days before it's permanently shredded.")}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="inspector-delete-cancel">{tr("Keep it")}</AlertDialogCancel>
                      <AlertDialogAction data-testid="inspector-delete-confirm-btn" onClick={() => onDelete(doc)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                        {tr("Delete")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              <Button data-testid="inspector-close-btn" variant="ghost" onClick={onClose} className={!readOnly && onDelete ? "" : "flex-1"}>
                {tr("Close")}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
